import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Grid,
  Box,
  Divider,
} from '@mui/material';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import productThumbnails from '../../products-page/constants/productThumbnails';

interface OrderSummaryProps {
  cartData: any;
}

/* ──────────────────────────────── helpers ──────────────────────────────── */
/** Return the price we should actually charge right now. */
const getEffectivePrice = (product: any): number => {
  if (!product) return 0;

  const { fullPrice, sale } = product;
  const now = Date.now();

  // A product is “on sale” if:
  //   – we have a sale object
  //   – salePrice < fullPrice
  //   – (optionally) sale.until is in the future
  if (
    sale &&
    typeof sale.salePrice === 'number' &&
    sale.salePrice < fullPrice &&
    (!sale.until || new Date(sale.until).getTime() > now)
  ) {
    return sale.salePrice;
  }
  return fullPrice;
};

/** Is this product currently discounted? */
const isOnSale = (product: any): boolean =>
  getEffectivePrice(product) !== product.fullPrice;

const OrderSummary: React.FC<OrderSummaryProps> = ({ cartData }) => {
  const items = cartData?.items || [];

  // const subtotal = items.reduce(
  //   (sum: number, item: any) => sum + item.productId.fullPrice * item.quantity,
  //   0,
  // );
  const subtotal = items.reduce(
    (sum: number, item: any) =>
      sum + getEffectivePrice(item.productId) * item.quantity,
    0,
  );

  const totalDiscount = items.reduce((sum: number, item: any) => {
    const discount = item.discountId;
    const price = item.productId.fullPrice;
    const qty = item.quantity;

    if (!discount) return sum;

    if (discount.amountUnits === 'percent') {
      return sum + price * qty * (discount.amount * 0.01);
    }

    if (discount.amountUnits === 'dollars') {
      return sum + discount.amount * qty;
    }

    return sum;
  }, 0);

  const total = subtotal - totalDiscount;

  return (
    <Card>
      <CardContent>
        <List>
          {items.map((item: any) => {
            const image = productThumbnails.get(item.productId.productId) || '';
            const { name } = item.productId;
            const { platform } = item;
            const hostApp = item.hostApplication?.displayName || 'Unknown';
            const { quantity } = item;
            // const lineTotal = item.productId.fullPrice * quantity;

            const unit = getEffectivePrice(item.productId);
            const lineTotal = unit * quantity;

            return (
              <ListItem key={item.productId._id || item.productId}>
                <ListItemAvatar>
                  <Avatar
                    variant="square"
                    src={image}
                    alt={name}
                    sx={{
                      height: 100,
                      width: 100,
                      mr: 2,
                      border: '1px solid #ccc',
                    }}
                  />
                </ListItemAvatar>
                <Box>
                  <Typography variant="subtitle1">{name}</Typography>
                  {/* <Typography variant="body2">Platform: {platform}, Host App: {hostApp}</Typography> */}
                  <Typography variant="body2">
                    {platform}, {hostApp}
                  </Typography>
                  <Typography variant="body2">x {quantity}</Typography>
                  {/* <Typography variant="body2" sx={{ color: '#1E88E5' }}>
                    {formatCurrency(lineTotal)}
                  </Typography> */}
                  <Typography variant="body2" sx={{ color: '#1E88E5' }}>
                    {isOnSale(item.productId) && (
                      <span
                        style={{
                          textDecoration: 'line-through',
                          opacity: 0.6,
                          marginRight: '6px',
                        }}
                      >
                        {formatCurrency(item.productId.fullPrice)}
                      </span>
                    )}
                    {formatCurrency(lineTotal)}
                  </Typography>
                  {/* <Typography variant="body2">Platform: {platform} | Host App: {hostApp}</Typography> */}
                  <Typography variant="body2" />
                </Box>
              </ListItem>
            );
          })}
        </List>

        <Box mt={2}>
          <Grid container>
            <Grid item xs={6}>
              <Typography>Subtotal</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right">{formatCurrency(subtotal)}</Typography>
            </Grid>

            {totalDiscount > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Discount</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" color="green">
                    -{formatCurrency(totalDiscount)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
          <Divider sx={{ my: 1 }} />
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Total</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" align="right">
                {formatCurrency(total)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
